/**
 * Domain-based feature flag config + resolver.
 * Baseline = defaults for all domains.
 * DOMAIN_OVERRIDES = per-domain overrides merged on top.
 * Resolution: effectiveFlags = { ...BASELINE, ...overrides[domain] }
 *
 * MUST stay in sync with fsds-be/src/config/feature-flags.config.ts
 */

export type FeatureKey =
  | 'mode.editor'
  | 'capability.builder.full'
  | 'capability.generatedContent'
  | 'scope.edit.landingPage'
  | 'scope.edit.otherPages'
  | 'page.template'
  | 'page.analytics'
  | 'action.publish'
  | 'action.customDomain'

/**
 * Server-resolved capability: true only when proxy, signing key, and shared secret
 * are all configured. Injected by next.config.js via resolvePresentonCapabilityState().
 * Domain overrides (below) can still force it off per-domain.
 */
const serverCapabilityEnabled =
  typeof process !== 'undefined' &&
  process.env.NEXT_PUBLIC_PRESENTON_CAPABILITY_STATE === 'true'

const BASELINE_FEATURES: Record<FeatureKey, boolean> = {
  'mode.editor': true,
  'capability.builder.full': true,
  'capability.generatedContent': serverCapabilityEnabled,
  'scope.edit.landingPage': true,
  'scope.edit.otherPages': true,
  'page.template': true,
  'page.analytics': true,
  'action.publish': true,
  'action.customDomain': true,
}

const DOMAIN_OVERRIDES: Record<string, Partial<Record<FeatureKey, boolean>>> = {
  'fullstackdatascience.com': {
    'mode.editor': false,
    'action.customDomain': false,
  },
}

/** Strip port and normalize to base domain */
function extractBaseDomain(hostname: string): string {
  const h = hostname.split(':')[0].toLowerCase()
  // fullstackdatascience.com and *.fullstackdatascience.com → fullstackdatascience.com
  if (h === 'fullstackdatascience.com' || h.endsWith('.fullstackdatascience.com')) {
    return 'fullstackdatascience.com'
  }
  return h
}

export function resolveFeatureFlags(hostname: string): Record<string, boolean> {
  const domain = extractBaseDomain(hostname)
  const overrides = DOMAIN_OVERRIDES[domain] ?? {}
  return { ...BASELINE_FEATURES, ...overrides }
}

export function canFeature(hostname: string, key: string): boolean {
  return resolveFeatureFlags(hostname)[key] ?? false
}
